import React, {
  useMemo,
} from 'react';
import {
  DateTime,
} from 'luxon';
import i18n from 'i18next';
import * as R from 'ramda';

import {
  Avatar,
} from 'web-components';

import {
  Flex,
  Text,
} from 'web-styled';

import Tab from '../../Tab';
import EmptyList from '../../EmptyList';
import * as S from './styled';

const DAY_FORMAT = 'EEEE, MMMM d';
const formatDate = (isoDay, f) => DateTime.fromISO(isoDay).toFormat(f);

const getPercent = (value) => {
  const percent = 100 * value;
  const number = Number(`${Math.round(`${percent}e2`)}e-2`) || 0;
  return `${number}%`;
};

type Props = {
  workoutEvents: {
    [string]: any[],
  },
  missedWorkouts: any[],
  clientsPerformance: any[],
};

const Tables = ({
  workoutEvents,
  missedWorkouts,
  clientsPerformance,
}: Props) => {
  const tabs = useMemo(() => ([
    {
      id: 1,
      name: i18n.t('dashboard.workout.this.week'),
      tooltip: i18n.t('tooltip.dashboard.workout.this.week'),
    },
    {
      id: 2,
      name: i18n.t('dashboard.client.performance'),
      tooltip: i18n.t('tooltip.dashboard.client.performance'),
    },
    {
      id: 3,
      name: i18n.t('dashboard.missed.workouts'),
      tooltip: i18n.t('tooltip.dashboard.missed.workouts'),
    },
  ]));

  return (
    <Tab tabs={tabs}>
      {activeTab => ({
        1: R.isEmpty(workoutEvents)
          ? (
            <EmptyList
              name="$t(workout) events"
            />
          ) : (
            <S.Day>
              {Object.keys(workoutEvents).map(day => (
                <div key={day}>
                  <Flex padding="0 0 0 21px">
                    <Text
                      size="16px"
                      color="#404447"
                      weight="700"
                    >
                      {formatDate(day, DAY_FORMAT)}
                    </Text>
                  </Flex>
                  <table>
                    <thead>
                      <tr>
                        <th>{i18n.format(i18n.t('client'), 'cap')}</th>
                        <th>{`Performed ${i18n.t('workout')}`}</th>
                        <th>Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workoutEvents[day].map(ev => (
                        <tr key={ev.id}>
                          <td>
                            <S.ThLink to={`/users/${ev.person_id}`}>
                              <Avatar
                                src={ev.person_avatar}
                                width="24px"
                                height="24px"
                                bgColor="#b0c237"
                                placeholderSize={13}
                              />
                              <span>
                                {ev.person_name}
                              </span>
                            </S.ThLink>
                          </td>
                          <td>
                            <S.ThLink
                              to={`/events/${ev.id}`}
                              missed={ev.is_completed ? '0' : '1'}
                            >
                              {ev.workout_name}
                            </S.ThLink>
                          </td>
                          <td>{formatDate(ev.begins_at, 'T')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </S.Day>
          ),
        2: (clientsPerformance.length > 0) ? (
          <table>
            <thead>
              <tr>
                <th>{i18n.format(i18n.t('client'), 'cap')}</th>
                <th>Last Week</th>
                <th>This Week</th>
              </tr>
            </thead>
            <tbody>
              {clientsPerformance.map(client => (
                <tr key={client.id}>
                  <td>
                    <S.ThLink to={`/users/${client.id}/programs`}>
                      <Avatar
                        src={client.avatar_thumb_url}
                        width="24px"
                        height="24px"
                        bgColor="#b0c237"
                        placeholderSize={13}
                      />
                      <span>
                        {client.full_name}
                      </span>
                    </S.ThLink>
                  </td>
                  <td>
                    {getPercent(client.last_week_performance)}
                  </td>
                  <td>
                    <S.DynamicCounter
                      positive={client.last_week_performance < client.this_week_performance}
                    >
                      {getPercent(client.this_week_performance)}
                      {client.last_week_performance < client.this_week_performance ? (
                        <S.PositiveDynamic />
                      ) : (
                        <S.NegativeDynamic />
                      )}
                    </S.DynamicCounter>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyList
            name="clients"
          />
        ),
        3: (missedWorkouts.length > 0) ? (
          <table>
            <thead>
              <tr>
                <th>{i18n.format(i18n.t('client'), 'cap')}</th>
                <th>{i18n.t('workout.missed')}</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {missedWorkouts.map(ev => (
                <tr key={ev.id}>
                  <td>
                    <S.ThLink to={`/users/${ev.person_id}/programs`}>
                      <Avatar
                        src={ev.person_avatar}
                        width="24px"
                        height="24px"
                        bgColor="#b0c237"
                        placeholderSize={13}
                      />
                      <span>
                        {ev.person_name}
                      </span>
                    </S.ThLink>
                  </td>
                  <td>
                    <S.ThLink missed="1" to={`/events/${ev.id}`}>
                      {ev.workout_name}
                    </S.ThLink>
                  </td>
                  <td>{formatDate(ev.begins_at, 'T')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyList
            name="$t(workout) events"
          />
        ),
      }[activeTab])}
    </Tab>
  );
};

export default Tables;
